import apiRequest, { ApiResponse } from './config';

/**
 * Persistent, client-generated device id (for soft device binding).
 * Stored in localStorage under 'alr_device_id'. If the user clears storage,
 * the next login will look like a new device (server will flag it for admin review).
 */
const DEVICE_ID_KEY = 'alr_device_id';

export function getOrCreateDeviceId(): string {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return '';
    const existing = window.localStorage.getItem(DEVICE_ID_KEY);
    if (existing && existing.length >= 16) return existing;
    // Prefer crypto.randomUUID where available; fall back to random hex.
    const g: any = typeof globalThis !== 'undefined' ? globalThis : {};
    const id =
      g?.crypto?.randomUUID?.() ||
      Array.from(g?.crypto?.getRandomValues?.(new Uint8Array(16)) || [])
        .map((b: any) => (b as number).toString(16).padStart(2, '0'))
        .join('') ||
      Math.random().toString(36).slice(2) + Date.now().toString(36);
    window.localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return '';
  }
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  phone?: string;
  address?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface User {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  role: 'customer' | 'admin';
  isActive: boolean;
  isApproved: boolean;
  createdAt: string;
  updatedAt: string;
  loginCodeLast4?: string | null;
  loginCodeEnabled?: boolean;
  loginCodeGeneratedAt?: string | null;
  lastLoginCodeAt?: string | null;
  hasUnfamiliarDeviceLogin?: boolean;
  lastUnfamiliarDeviceAt?: string | null;
}

export interface AuthResponse {
  user: User;
  token: string;
}

class AuthService {
  async register(data: RegisterData): Promise<ApiResponse<AuthResponse>> {
    return apiRequest<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async login(data: LoginData): Promise<ApiResponse<AuthResponse>> {
    return apiRequest<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async loginWithMobile(data: { phone: string; otp: string }): Promise<ApiResponse<AuthResponse>> {
    return apiRequest<AuthResponse>('/auth/login-mobile', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Code-only login for customers. Sends a locally-stored deviceId for soft device binding.
   * `website` is a honeypot field — must always be empty.
   */
  async loginWithCode(data: {
    code: string;
    deviceId?: string;
    website?: string;
  }): Promise<ApiResponse<AuthResponse>> {
    return apiRequest<AuthResponse>('/auth/login-code', {
      method: 'POST',
      body: JSON.stringify({
        code: data.code,
        deviceId: data.deviceId || getOrCreateDeviceId(),
        website: data.website || '',
      }),
    });
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    return apiRequest<User>('/auth/me', {
      method: 'GET',
    });
  }

  async updateProfile(data: Partial<User>): Promise<ApiResponse<User>> {
    return apiRequest<User>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  setAuth(token: string, user: User): void {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  getUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

export default new AuthService();

